import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/useAuth.js';
import { Badge } from '../components/ui/Badge.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { financeApi } from '../services/financeApi.js';
import { hrApi } from '../services/hrApi.js';
import { salesApi } from '../services/salesApi.js';

const pad = (n) => String(n).padStart(2, '0');
const keyOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const norm = (v) => (v ? String(v).slice(0, 10) : null); // API date → YYYY-MM-DD

const TYPE = {
  invoice: 'warning',
  maintenance: 'info',
  order: 'neutral',
  leave: 'success',
  event: 'danger',
};

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return keyOf(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

function emptyEventForm(date) {
  return { title: '', eventDate: date, endDate: '', description: '' };
}

export function CalendarPage() {
  const { t, locale } = useTranslation();
  const { user, roles } = useAuth();
  const queryClient = useQueryClient();
  const now = useMemo(() => new Date(), []);
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  // Day whose entries are shown in the popup (YYYY-MM-DD), null = closed.
  const [dayKey, setDayKey] = useState(null);
  const [eventForm, setEventForm] = useState(null); // null = form closed
  const [formError, setFormError] = useState(null);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';

  const sources = useQueries({
    queries: [
      { queryKey: ['cal', 'invoices'], queryFn: () => financeApi.listInvoices({ limit: 200 }).then((r) => r.items), retry: false },
      { queryKey: ['cal', 'plans'], queryFn: () => financeApi.listMaintenancePlans({ active: true }).then((r) => r.items), retry: false },
      { queryKey: ['cal', 'mo'], queryFn: () => financeApi.listMaintenanceOrders().then((r) => r.items), retry: false },
      { queryKey: ['cal', 'orders'], queryFn: () => salesApi.listOrders({ limit: 200 }).then((r) => r.items), retry: false },
      { queryKey: ['cal', 'leave'], queryFn: () => hrApi.listLeaveRequests({ status: 'APPROVED' }).then((r) => r.items), retry: false },
    ],
  });
  const [invoices, plans, mOrders, orders, leave] = sources.map((s) => s.data || []);

  // Company-wide entries for the visible month.
  const monthFrom = keyOf(view.y, view.m, 1);
  const monthTo = keyOf(view.y, view.m, new Date(view.y, view.m + 1, 0).getDate());
  const companyEvents = useQuery({
    queryKey: ['cal', 'events', monthFrom],
    queryFn: () => hrApi.listCompanyEvents({ from: monthFrom, to: monthTo }).then((r) => r.items || []),
    retry: false,
  });

  const createEvent = useMutation({
    mutationFn: (body) => hrApi.createCompanyEvent(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cal', 'events'] });
      setEventForm(null);
      setFormError(null);
    },
    onError: (err) => setFormError(err?.message || t('common.loadError')),
  });
  const deleteEvent = useMutation({
    mutationFn: (id) => hrApi.deleteCompanyEvent(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cal', 'events'] }),
    onError: (err) => setFormError(err?.message || t('common.loadError')),
  });

  // Events keyed by YYYY-MM-DD.
  const byDate = useMemo(() => {
    const map = {};
    const push = (date, ev) => {
      if (!date) return;
      (map[date] = map[date] || []).push(ev);
    };
    for (const inv of invoices) {
      if (inv.status === 'PAID' || inv.status === 'CANCELLED') continue;
      push(norm(inv.dueDate), { type: 'invoice', label: inv.invoiceNumber, route: `/finance/invoices/${inv.id}` });
    }
    for (const p of plans) push(norm(p.nextDue), { type: 'maintenance', label: `${p.planNumber} ⟳`, route: '/finance/maintenance' });
    for (const o of mOrders) {
      if (o.status === 'COMPLETED' || o.status === 'CANCELLED') continue;
      push(norm(o.plannedDate), { type: 'maintenance', label: o.orderNumber, route: '/finance/maintenance' });
    }
    for (const o of orders) {
      if (o.status === 'CANCELLED') continue;
      push(norm(o.requiredDate), { type: 'order', label: o.orderNumber, route: `/sales/orders/${o.id}` });
    }
    for (const lv of leave) {
      let d = norm(lv.startDate);
      const end = norm(lv.endDate);
      for (let i = 0; d && end && d <= end && i < 60; i += 1) {
        push(d, { type: 'leave', label: lv.employeeName || t('cal.type.leave'), route: '/hr/leave' });
        d = addDays(d, 1);
      }
    }
    for (const ev of companyEvents.data || []) {
      let d = norm(ev.eventDate);
      const end = norm(ev.endDate) || d;
      for (let i = 0; d && d <= end && i < 60; i += 1) {
        push(d, { type: 'event', label: ev.title, entry: ev });
        d = addDays(d, 1);
      }
    }
    // Absences always come first within a day; the rest keeps its order.
    for (const list of Object.values(map)) {
      list.sort((a, b) => (a.type === 'leave' ? 0 : 1) - (b.type === 'leave' ? 0 : 1));
    }
    return map;
  }, [invoices, plans, mOrders, orders, leave, companyEvents.data, t]);

  // Month grid (Monday-first), padded to full weeks.
  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const lead = (first.getDay() + 6) % 7;
    const days = new Date(view.y, view.m + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < lead; i += 1) out.push(null);
    for (let d = 1; d <= days; d += 1) out.push(d);
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view]);

  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i))); // 2024-01-01 = Monday
  }, [locale]);
  const monthLabel = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(view.y, view.m, 1));
  const todayKey = keyOf(now.getFullYear(), now.getMonth(), now.getDate());

  const move = (delta) => setView((v) => {
    const dt = new Date(v.y, v.m + delta, 1);
    return { y: dt.getFullYear(), m: dt.getMonth() };
  });

  const dayEvents = (dayKey && byDate[dayKey]) || [];
  const dayLabel = dayKey
    ? new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${dayKey}T12:00:00`))
    : '';

  function openDay(key) {
    setDayKey(key);
    setEventForm(null);
    setFormError(null);
  }

  function submitEvent(e) {
    e.preventDefault();
    if (!eventForm?.title.trim()) return;
    createEvent.mutate({
      title: eventForm.title.trim(),
      eventDate: eventForm.eventDate,
      endDate: eventForm.endDate || undefined,
      description: eventForm.description || undefined,
      createdByName: displayName,
    });
  }

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('cal.title')}</h1>
          <p className="muted">{t('cal.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" onClick={() => move(-1)}>←</Button>
          <Button variant="ghost" onClick={() => setView({ y: now.getFullYear(), m: now.getMonth() })}>{t('cal.today')}</Button>
          <Button variant="ghost" onClick={() => move(1)}>→</Button>
        </div>
      </header>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <strong style={{ textTransform: 'capitalize' }}>{monthLabel}</strong>
        <span style={{ flex: 1 }} />
        {Object.entries(TYPE).map(([type, variant]) => (
          <span key={type} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <Badge variant={variant}>{t(`cal.type.${type}`)}</Badge>
          </span>
        ))}
      </div>

      <div className="cal-grid">
        {weekdays.map((w) => (
          <div key={w} className="cal-head muted">{w}</div>
        ))}
        {cells.map((d, i) => {
          const key = d ? keyOf(view.y, view.m, d) : null;
          const events = (key && byDate[key]) || [];
          const isToday = key === todayKey;
          return (
            <div
              key={i}
              className={`cal-cell${d ? '' : ' cal-empty'}`}
              onClick={() => {
                // The whole day is one click target: always open the popup.
                // Document links live inside the popup ("Öffnen").
                if (d) openDay(key);
              }}
            >
              {d && (
                <>
                  <div style={{ fontSize: '0.8rem', fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--color-accent, #1d4ed8)' : 'inherit' }}>{d}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' }}>
                    {events.slice(0, 3).map((ev, idx) => (
                      <span key={idx} title={ev.label}>
                        <Badge variant={TYPE[ev.type]}><span style={{ fontSize: '0.7rem' }}>{ev.label}</span></Badge>
                      </span>
                    ))}
                    {events.length > 3 && <span className="muted" style={{ fontSize: '0.7rem' }}>+{events.length - 3}</span>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <Modal
        open={Boolean(dayKey)}
        onClose={() => setDayKey(null)}
        title={dayLabel}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDayKey(null)}>{t('common.close')}</Button>
            {!eventForm && (
              <Button variant="primary" onClick={() => { setEventForm(emptyEventForm(dayKey)); setFormError(null); }}>
                {t('cal.event.add')}
              </Button>
            )}
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {dayEvents.length === 0 && !eventForm && <p className="muted" style={{ margin: 0 }}>{t('cal.day.empty')}</p>}

          {dayEvents.map((ev, idx) => (
            <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 4, borderBottom: '1px solid var(--color-hairline)', paddingBottom: 'var(--space-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Badge variant={TYPE[ev.type]}>{t(`cal.type.${ev.type}`)}</Badge>
                <strong>{ev.label}</strong>
                <span style={{ flex: 1 }} />
                {ev.route && <Link to={ev.route}>{t('cal.day.open')}</Link>}
                {ev.entry && (user?.id === ev.entry.createdBy || roles.includes('admin')) && (
                  <Button size="sm" variant="danger" disabled={deleteEvent.isPending} onClick={() => deleteEvent.mutate(ev.entry.id)}>
                    {t('common.delete')}
                  </Button>
                )}
              </div>
              {ev.entry?.description && <p className="muted text-sm" style={{ margin: 0 }}>{ev.entry.description}</p>}
              {ev.entry?.createdByName && (
                <span className="muted text-sm">{t('cal.event.createdBy', { name: ev.entry.createdByName })}</span>
              )}
            </div>
          ))}

          {eventForm && (
            <form onSubmit={submitEvent} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', borderTop: dayEvents.length ? '1px solid var(--color-hairline)' : 'none', paddingTop: 'var(--space-2)' }}>
              <strong>{t('cal.event.new')}</strong>
              <label className="field">
                <span className="field-label">{t('cal.event.title')}</span>
                <input className="input" value={eventForm.title} onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))} required autoFocus />
              </label>
              <div className="field-row">
                <label className="field">
                  <span className="field-label">{t('cal.event.date')}</span>
                  <input className="input" type="date" value={eventForm.eventDate} onChange={(e) => setEventForm((f) => ({ ...f, eventDate: e.target.value }))} required />
                </label>
                <label className="field">
                  <span className="field-label">{t('cal.event.endDate')}</span>
                  <input className="input" type="date" value={eventForm.endDate} onChange={(e) => setEventForm((f) => ({ ...f, endDate: e.target.value }))} />
                </label>
              </div>
              <label className="field">
                <span className="field-label">{t('cal.event.description')}</span>
                <textarea className="textarea" rows={2} value={eventForm.description} onChange={(e) => setEventForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={() => setEventForm(null)}>{t('common.cancel')}</Button>
                <Button variant="primary" onClick={submitEvent} disabled={createEvent.isPending}>
                  {createEvent.isPending ? `${t('common.save')}…` : t('common.save')}
                </Button>
              </div>
            </form>
          )}

          {formError && <div className="alert danger">{formError}</div>}
        </div>
      </Modal>
    </section>
  );
}
